// ./src/config/tireOptions.ts
// Tire-related options configuration
// Used in selector components for tire sets

import { TIRE_TYPE, TIRE_SET_STATUS, TIRE_POSITION, TIRE_CONDITION } from "@/utils/api";

// ============================================
// Types
// ============================================

export interface TireOption {
  /** Value stored in database */
  value: string | number;
  /** Translation key for label - use with t('tireSets:tireType.winter') */
  labelKey: string;
  /** Icon name (Tabler icons) */
  icon?: string;
  /** Theme color */
  color?: string;
}

// ============================================
// Tire Type Options
// ============================================

export const TIRE_TYPE_OPTIONS: TireOption[] = [
  { value: TIRE_TYPE.SUMMER, labelKey: "tireType.summer", icon: "Sun", color: "orange" },
  { value: TIRE_TYPE.WINTER, labelKey: "tireType.winter", icon: "Snowflake", color: "blue" },
  { value: TIRE_TYPE.ALL_SEASON, labelKey: "tireType.allSeason", icon: "Leaf", color: "teal" },
  { value: TIRE_TYPE.ALL_WEATHER, labelKey: "tireType.allWeather", icon: "CloudRain", color: "cyan" },
  { value: TIRE_TYPE.PERFORMANCE, labelKey: "tireType.performance", icon: "Flame", color: "red" },
  { value: TIRE_TYPE.OFF_ROAD, labelKey: "tireType.offRoad", icon: "Mountain", color: "yellow" },
];

export const TIRE_TYPE_MAP: Record<string, TireOption> = Object.fromEntries(
  TIRE_TYPE_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Tire Set Status Options
// ============================================

export const TIRE_SET_STATUS_OPTIONS: TireOption[] = [
  { value: TIRE_SET_STATUS.ACTIVE, labelKey: "status.active", icon: "CircleCheck", color: "teal" },
  { value: TIRE_SET_STATUS.STORED, labelKey: "status.stored", icon: "Package", color: "blue" },
  { value: TIRE_SET_STATUS.RETIRED, labelKey: "status.retired", icon: "CircleX", color: "gray" },
];

export const TIRE_SET_STATUS_MAP: Record<string, TireOption> = Object.fromEntries(
  TIRE_SET_STATUS_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Tire Position Options
// ============================================

export const TIRE_POSITION_OPTIONS: TireOption[] = [
  { value: TIRE_POSITION.ALL, labelKey: "position.all", color: "gray" },
  { value: TIRE_POSITION.FRONT, labelKey: "position.front", color: "blue" },
  { value: TIRE_POSITION.REAR, labelKey: "position.rear", color: "orange" },
];

export const TIRE_POSITION_MAP: Record<string, TireOption> = Object.fromEntries(
  TIRE_POSITION_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Tire Condition Options
// ============================================

export const TIRE_CONDITION_OPTIONS: TireOption[] = [
  { value: TIRE_CONDITION.NEW, labelKey: "condition.new", color: "teal" },
  { value: TIRE_CONDITION.USED, labelKey: "condition.used", color: "yellow" },
  { value: TIRE_CONDITION.CAME_WITH_VEHICLE, labelKey: "condition.cameWithVehicle", color: "gray" },
];

export const TIRE_CONDITION_MAP: Record<string, TireOption> = Object.fromEntries(
  TIRE_CONDITION_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Helper Functions
// ============================================

export const getTireTypeOption = (value: string | null | undefined): TireOption | undefined => {
  if (!value) return undefined;
  return TIRE_TYPE_MAP[value];
};

export const getTireSetStatusOption = (value: string | null | undefined): TireOption | undefined => {
  if (!value) return undefined;
  return TIRE_SET_STATUS_MAP[value];
};

export const getTirePositionOption = (value: string | null | undefined): TireOption | undefined => {
  if (!value) return undefined;
  return TIRE_POSITION_MAP[value];
};

export const getTireConditionOption = (value: string | null | undefined): TireOption | undefined => {
  if (!value) return undefined;
  return TIRE_CONDITION_MAP[value];
};